import styleText from './as-modal-overlay.css?raw';
import { sendEv } from '../../data/events.ts';

/** Longest transition on the element, in ms, 0 if it has none. */
function transitionMs(el: Element): number {
  const durations = getComputedStyle(el).transitionDuration || '';
  return durations.split(',').reduce((longest, part) => {
    const text = part.trim();
    const ms = text.endsWith('ms') ? parseFloat(text) : parseFloat(text) * 1000;
    return Number.isFinite(ms) ? Math.max(longest, ms) : longest;
  }, 0);
}

// Some browsers ship a password-manager content script that monkey-patches
// HTMLDialogElement.prototype.showModal() and scans the dialog for forms as
// part of that patched call. On Firefox iOS that scanner assumes real DOM
// containment between <dialog> and any <form> inside it. In our default
// (shadow-DOM + <slot>) mode the panel content is only slotted, not a true
// light-DOM descendant of the shadow <dialog>, so the scanner resolves an
// undefined form and throws (as-home saw this crash the sign-in modal).
// The `light-dom` attribute renders without a shadow root at all, moving
// panel children to be real descendants of a real <dialog>, sidestepping
// that class of bug entirely. Default (no attribute) behavior is unchanged.
export class AsModalOverlay extends HTMLElement {
  static readonly tag = 'as-modal-overlay' as const;

  private _dialog: HTMLDialogElement | null = null;
  // The white card the panels sit in. It has a background and padding of its
  // own, so an empty one is a visible box rather than nothing.
  private _contentBox: HTMLElement | null = null;
  private _panelObserver: MutationObserver | null = null;
  private _isLightDom = false;
  // Light-DOM mode only: the element that actually holds the panel children
  // (shadow mode doesn't need this — panels stay as `this`'s own children
  // and are merely projected into the shadow <dialog> via <slot>).
  private _contentHost: HTMLElement | null = null;
  // Element we last dynamically tagged [autofocus] on, so it can be cleared
  // before the next open picks a (possibly different) target.
  private _dynamicAutofocusEl: HTMLElement | null = null;
  // Set while we close the <dialog> ourselves (close(), or the show attribute
  // being removed), so the close handler can tell our close from the browser's.
  private _closingViaApi = false;
  // Armed by open({ delayMs }), disarmed by close() and by the next open.
  private _pendingOpen: ReturnType<typeof setTimeout> | null = null;
  // Set while a delayed child is waiting to be shown: no panel is displayed,
  // so an open overlay shows its backdrop and nothing else.
  private _hideUntilReveal = false;

  static get observedAttributes() {
    return ['show', 'visible-child', 'mode', 'show-close', 'disable-bg-close'];
  }

  constructor() {
    super();
    this._isLightDom = this.hasAttribute('light-dom');
    if (!this._isLightDom) {
      this.attachShadow({ mode: 'open' });
    }
  }

  /** Root to query/attach delegated listeners on: the shadow root, or the host itself in light-DOM mode. */
  private get _root(): ParentNode {
    return this._isLightDom ? this : this.shadowRoot!;
  }

  private _clearPendingOpen(): void {
    this._hideUntilReveal = false;
    if (this._pendingOpen === null) return;
    clearTimeout(this._pendingOpen);
    this._pendingOpen = null;
  }

  /** The element whose direct children are the panels to show/hide. */
  private get _panelContainer(): Element {
    return this._isLightDom ? this._contentHost! : this;
  }

  connectedCallback() {
    this._render();

    // disable-bg-close means the panel's own buttons are the only way out, so
    // Escape is refused too. The cancel event covers the normal case. Chrome's
    // close-watcher rules let a second Escape close the dialog anyway when the
    // page has seen no user activation since the last one, and a closed
    // blocking dialog would silently unblock the app - so a close we didn't
    // ask for is reopened on the spot.
    this._dialog?.addEventListener('cancel', (e) => {
      if (this.hasAttribute('disable-bg-close')) e.preventDefault();
    });

    this._dialog?.addEventListener('close', () => {
      if (!this._closingViaApi && this.hasAttribute('disable-bg-close') && this.hasAttribute('show')) {
        this._dialog!.showModal();
        return;
      }
      this._closingViaApi = false;
      if (this.hasAttribute('show')) this.removeAttribute('show');
      sendEv(this, 'as-modal-closed');
    });

    this._root.addEventListener('click', (e) => {
      const path = (e as MouseEvent).composedPath();
      const isCloseBtn = path.some(el => (el as HTMLElement).classList?.contains('close-button'));
      const isBackdrop = e.target === this._dialog;

      if (isCloseBtn || (isBackdrop && !this.hasAttribute("disable-bg-close"))) {
        this.close();
      }
    });

    // Tapping a non-field control (a nav link, a plain button) while the
    // dialog is already modal would otherwise let the browser's default
    // mousedown handling blur whatever field currently holds focus. With
    // nothing else in the tap target focusable, some browsers' modal
    // focus-trap then falls back to focusing the <dialog> itself to keep
    // focus contained — a <dialog> has no .form, which is what crashes
    // Firefox iOS's password-manager script. This runs before our own
    // click handlers (and therefore before _syncVisibility()'s own focus
    // relocation), so the fix has to sit on mousedown: keep whatever field
    // is already focused focused through the tap, and let our click
    // handler's own panel switch (if any) do the actual, deliberate
    // relocation afterward. Scoped to only when there's actually a focused
    // field at risk of being blurred — otherwise this would preventDefault()
    // (and so block the browser's default focus-on-click) for every plain
    // click anywhere in the modal, including e.g. buttons and dropdowns.
    this._root.addEventListener('mousedown', (e) => {
      if (!this._dialog?.open) return;
      const activeEl = document.activeElement as HTMLElement | null;
      if (!activeEl?.matches('input, textarea, select')) return;

      const target = e.target as HTMLElement;
      if (!target.closest('input, textarea, select')) {
        e.preventDefault();
      }
    });

    this.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        sendEv(this, 'as-modal-enter-pressed');
      }
    });

    this._panelObserver = new MutationObserver(() => this._syncVisibility());
    this._panelObserver.observe(this._panelContainer, { childList: true });

    this.addEventListener('as-set-visible-child', (e: any) => this.setAttribute('visible-child', e.detail.id));
    ['open-modal', 'as-open-overlay', 'as-open-modal'].forEach(sig =>
      this.addEventListener(sig, (e: any) => this.open(e.detail?.visibleChild))
    );
    ['close-modal', 'as-close-overlay', 'as-hide-overlay'].forEach(sig =>
      this.addEventListener(sig, () => this.close())
    );

    this._syncVisibility();
    if (this.hasAttribute('show')) this.open();
  }

  disconnectedCallback() {
    this._panelObserver?.disconnect();
  }

  private _render() {
    const showClose = this.getAttribute("show-close") !== "false" && this.hasAttribute("show-close");
    const dialogMarkup = `
      <dialog id="modal-container">
        <div class="modal-content">
          ${showClose ? `<button class="close-button" aria-label="Close">×</button>` : ''}
          ${this._isLightDom ? `<div class="modal-content-host"></div>` : `<slot id="content-slot"></slot>`}
        </div>
      </dialog>
    `;

    if (this._isLightDom) {
      // Preserve panel children across re-renders (e.g. a show-close toggle) —
      // on first render they're still this element's own children; after
      // that they live in the previous contentHost.
      const preserved = this._contentHost
        ? Array.from(this._contentHost.childNodes)
        : Array.from(this.childNodes);

      this.innerHTML = `<style>${styleText}</style>${dialogMarkup}`;

      this._contentHost = this.querySelector('.modal-content-host') as HTMLElement;
      preserved.forEach(node => this._contentHost!.appendChild(node));
    } else {
      this.shadowRoot!.innerHTML = `<style>${styleText}</style>${dialogMarkup}`;
    }

    // Re-queried on every render: a show-close toggle rebuilds the markup and
    // would otherwise leave both pointing at elements no longer in the tree.
    this._dialog = this._root.querySelector('dialog');
    this._contentBox = this._root.querySelector('.modal-content');
  }

  attributeChangedCallback(name: string, old: string, val: string) {
    if (old === val || !this._dialog) return;

    if (name === 'show') {
      if (val !== null) {
        if (!this._dialog.open) {
          this._ensureAutofocusTarget();
          try {
            this._dialog.showModal();
          } catch (err) {
            // Defensive fallback: a third-party script's monkey-patched
            // showModal() threw before delegating to the real one. Fall
            // back to a non-modal open so the dialog isn't left stuck shut.
            console.error('as-modal-overlay: showModal() threw (likely a third-party script), opening as non-modal fallback', err);
            if (!this._dialog.open) this._dialog.show();
          }
        }
      } else {
        if (this._dialog.open) {
          this._closingViaApi = true;
          this._dialog.close();
        }
      }
    }

    if (name === 'visible-child' || name === 'mode') {
      this._syncVisibility();
    }

    if (name === 'show-close') this._render();
  }

  private _syncVisibility() {
    if (this._isLightDom && !this._contentHost) return;

    const mode = this.getAttribute('mode') || 'auto';
    const targetId = this.getAttribute('visible-child');
    const children = Array.from(this._panelContainer.children) as HTMLElement[];
    // A child waiting on its delay is not shown yet, and nothing takes its place.
    if (this._contentBox) this._contentBox.style.display = this._hideUntilReveal ? 'none' : '';
    const newVisiblePanel = mode === 'auto' && !this._hideUntilReveal
      ? (children.find(el => el.id === targetId) ?? null)
      : null;
    let shouldDisableBg = false;

    // Show the target panel FIRST. focus() is a no-op on a display:none
    // element, so the new panel must already be rendered before we can
    // relocate focus into it (below) — only THEN is it safe to hide the
    // old one without ever leaving focus on an element about to disappear.
    if (mode === 'auto' && newVisiblePanel) {
      newVisiblePanel.style.display = '';
      newVisiblePanel.classList.add('visible');
      if (newVisiblePanel.hasAttribute('disable-bg-close')) shouldDisableBg = true;
    }

    // Switching the visible panel while the dialog is already open (e.g. the
    // sign-in form's "Register" link) is about to display:none the panel
    // that currently holds focus. A focused element that becomes unrenderable
    // triggers the browser's own focus-recovery, which — inside an open
    // modal <dialog> — falls back to focusing the dialog itself. That's the
    // exact precondition _ensureAutofocusTarget() guards against on open;
    // relocate focus to the new panel FIRST so that fallback never triggers.
    if (this._dialog?.open && newVisiblePanel) {
      const activeEl = document.activeElement as HTMLElement | null;
      const activeElLeavingView = activeEl && activeEl !== document.body && !newVisiblePanel.contains(activeEl);
      if (activeElLeavingView) {
        // Unconditional blur first: even if no safe focus target is found
        // below, the old (about-to-be-hidden) element is guaranteed to no
        // longer hold focus, so hiding it can't trigger the browser's
        // focus-recovery fallback to the dialog.
        activeEl.blur();
        const target = this._firstFocusable(newVisiblePanel);
        target?.focus();
        this._nudgeFocusAfterSettle(target);
      }
    }

    children.forEach(el => {
      if (mode === 'auto') {
        if (el !== newVisiblePanel) {
          el.style.display = 'none';
          el.classList.remove('visible');
        }
      } else {
        el.style.display = '';
      }
    });

    shouldDisableBg ? this.setAttribute('disable-bg-close', '') : this.removeAttribute('disable-bg-close');
  }

  /**
   * Prefers a genuine <input> over textarea/select/button: Firefox iOS's
   * password-manager script only treats a focus target as unconditionally
   * safe when it's an HTMLInputElement — anything else (including a button
   * or the <dialog> itself) falls through to code that reads `.form` and
   * crashes unless that resolves to a real, non-null form.
   */
  private _firstFocusable(panel: Element): HTMLElement | null {
    return (panel.querySelector('input') ?? panel.querySelector('textarea, select, button')) as HTMLElement | null;
  }

  /**
   * Firefox iOS's own password-manager script reads the newly-focused
   * field's geometry synchronously off `focusin` to position its native
   * keyboard accessory bar — a native overlay bridged out of the page
   * entirely, so nothing in our own layout can correct it directly. We just
   * focused this field mid-panel-switch, so that read may be against
   * stale/mid-transition coordinates. Once the browser has had a full
   * render pass, dispatch a synthetic `focusin` (never a real blur/focus —
   * that risks dismissing the keyboard when triggered outside the original
   * user gesture) so the script gets a second, accurate read.
   */
  private _nudgeFocusAfterSettle(target: HTMLElement | null) {
    if (!target) return;
    requestAnimationFrame(() => requestAnimationFrame(() => {
      if (document.activeElement === target) {
        target.dispatchEvent(new FocusEvent('focusin', { bubbles: true, composed: true }));
      }
    }));
  }

  /**
   * Per the HTML spec, showModal() always runs its own "dialog focusing
   * steps": focus the first [autofocus] descendant, or — if there is none —
   * the dialog itself. A <dialog> has no .form property, and at least one
   * real-world password-manager content script (Firefox iOS) dereferences
   * `.form` on whatever received focus without a null check, crashing on
   * that exact case. Tagging a real field [autofocus] beforehand steers the
   * browser's own algorithm there instead, so the dialog never becomes the
   * focus target in the first place.
   */
  private _ensureAutofocusTarget() {
    if (this._dynamicAutofocusEl) {
      this._dynamicAutofocusEl.removeAttribute('autofocus');
      this._dynamicAutofocusEl = null;
    }

    const panel = this._panelContainer.querySelector('.visible') as HTMLElement | null;
    if (!panel || panel.querySelector('[autofocus]')) return;

    const target = this._firstFocusable(panel);
    if (target) {
      target.setAttribute('autofocus', '');
      this._dynamicAutofocusEl = target;
    }
  }

  /**
   * `visible-child` moves the moment it is asked to, always. `delayMs` delays
   * only the showing of that child: an open overlay drops what it was showing
   * and sits on its darkened backdrop until the child appears, and a closed one
   * stays shut. close() disarms it, so work that finishes inside the delay puts
   * no panel on screen at all. A later open() replaces whatever is pending.
   */
  open(id?: string, opts: { delayMs?: number } = {}) {
    this._clearPendingOpen();
    // Before the attribute, so the change it triggers already knows to wait.
    this._hideUntilReveal = !!opts.delayMs;
    if (id) this.setAttribute('visible-child', id);
    if (!opts.delayMs) { this.setAttribute('show', ''); return; }
    // Again by hand: setting visible-child to what it already was fires nothing.
    this._syncVisibility();
    this._pendingOpen = setTimeout(() => {
      this._pendingOpen = null;
      this._hideUntilReveal = false;
      this.setAttribute('show', '');
      this._syncVisibility();
    }, opts.delayMs);
  }

  close() {
    this._clearPendingOpen();
    this.removeAttribute('show');
  }

  /** Resolves once the close transition has finished and the dialog is really
   *  off screen. The dialog keeps painting all through its fade, so anything
   *  that changes what it shows waits for this. Already open, or no transition
   *  to wait for, and it resolves right away. */
  whenHidden(): Promise<void> {
    const dialog = this._dialog;
    if (!dialog || dialog.open) return Promise.resolve();
    const ms = transitionMs(dialog);
    return ms > 0 ? new Promise(resolve => setTimeout(resolve, ms)) : Promise.resolve();
  }

  show(id?: string, opts: { delayMs?: number } = {}) { this.open(id, opts); }
  hide() { this.close(); }
  setVisibleChild(id: string) { this.setAttribute('visible-child', id); }

  /** Id of the child currently shown, or null while the overlay is closed. */
  get visibleChildId(): string | null {
    return this.hasAttribute('show') ? this.getAttribute('visible-child') : null;
  }
}

customElements.define(AsModalOverlay.tag, AsModalOverlay);
